import { writable } from 'svelte/store';
import type {
  TreeDocument,
  ResponsibleNode,
  CategoryNode,
  SubCategoryNode,
  DirectoryTreeParams,
} from '../api';
import { api } from '../api';

export type GroupMode = 'person' | 'flat';
export type SortOrder = 'asc' | 'desc';

const STORAGE_KEY_GROUP_MODE = 'plc_tree_group_mode';

function loadGroupMode(): GroupMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_GROUP_MODE);
    if (stored === 'person' || stored === 'flat') return stored;
    // Migrate old boolean key
    const legacy = localStorage.getItem('plc_tree_group_by_person');
    if (legacy !== null) {
      const mode: GroupMode = legacy === 'true' ? 'person' : 'flat';
      localStorage.setItem(STORAGE_KEY_GROUP_MODE, mode);
      localStorage.removeItem('plc_tree_group_by_person');
      return mode;
    }
    return 'person';
  } catch {
    return 'person';
  }
}

interface BrowseTreeState {
  treeData: ResponsibleNode[];
  groupMode: GroupMode;
  sortBy: string;
  sortOrder: SortOrder;
  expandedResponsible: Set<string>;
  expandedCategory: Set<string>;
  expandedSubCategory: Set<string>;
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

export function sortDocuments(
  docs: TreeDocument[],
  sortBy: string,
  sortOrder: SortOrder,
): TreeDocument[] {
  return [...docs].sort((a, b) => {
    let aVal: string | undefined;
    let bVal: string | undefined;

    if (sortBy === 'referenceDate') {
      aVal = a.referenceDate;
      bVal = b.referenceDate;
    } else if (sortBy === 'uploadedAt') {
      aVal = a.uploadedAt;
      bVal = b.uploadedAt;
    } else {
      return 0;
    }

    // Nulls sort last regardless of order
    if (!aVal && !bVal) return 0;
    if (!aVal) return 1;
    if (!bVal) return -1;

    const cmp = aVal.localeCompare(bVal);
    return sortOrder === 'asc' ? cmp : -cmp;
  });
}

export function mergeCategoriesFlat(treeData: ResponsibleNode[]): CategoryNode[] {
  const categoryMap = new Map<
    string,
    {
      node: CategoryNode;
      subCategoryMap: Map<string, SubCategoryNode>;
    }
  >();

  const seenDocIds = new Set<string>();

  for (const responsible of treeData) {
    for (const cat of responsible.categories) {
      let entry = categoryMap.get(cat.category);
      if (!entry) {
        entry = {
          node: { ...cat, subCategories: [], documentCount: 0 },
          subCategoryMap: new Map(),
        };
        categoryMap.set(cat.category, entry);
      }

      for (const sub of cat.subCategories) {
        let subEntry = entry.subCategoryMap.get(sub.subCategory);
        if (!subEntry) {
          subEntry = { ...sub, documents: [], documentCount: 0 };
          entry.subCategoryMap.set(sub.subCategory, subEntry);
        }

        for (const doc of sub.documents) {
          if (!seenDocIds.has(doc.id)) {
            seenDocIds.add(doc.id);
            subEntry.documents.push(doc);
            subEntry.documentCount++;
            entry.node.documentCount++;
          }
        }
      }
    }
  }

  return Array.from(categoryMap.values())
    .map(({ node, subCategoryMap }) => ({
      ...node,
      subCategories: Array.from(subCategoryMap.values()).sort((a, b) =>
        a.subCategory.localeCompare(b.subCategory),
      ),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
}

function createBrowseTreeStore() {
  const { subscribe, update } = writable<BrowseTreeState>({
    treeData: [],
    groupMode: loadGroupMode(),
    sortBy: 'referenceDate',
    sortOrder: 'desc',
    expandedResponsible: new Set(),
    expandedCategory: new Set(),
    expandedSubCategory: new Set(),
    loading: false,
    loaded: false,
    error: null,
  });

  let loadRun = 0;

  return {
    subscribe,

    setGroupMode(mode: GroupMode) {
      try {
        localStorage.setItem(STORAGE_KEY_GROUP_MODE, mode);
      } catch {
        // localStorage unavailable (e.g. private browsing) — ignore
      }
      update((s) => ({
        ...s,
        groupMode: mode,
        expandedResponsible: new Set(),
        expandedCategory: new Set(),
        expandedSubCategory: new Set(),
      }));
    },

    setSort(sortBy: string, sortOrder: SortOrder) {
      update((s) => ({ ...s, sortBy, sortOrder }));
    },

    async loadTree(params: DirectoryTreeParams = {}) {
      const run = ++loadRun;
      update((s) => ({ ...s, loading: true, error: null }));
      try {
        const response = await api.reports.getDirectoryTree(params);
        if (run !== loadRun) return;
        update((s) => ({
          ...s,
          treeData: response.tree,
          loading: false,
          loaded: true,
        }));
      } catch (err) {
        if (run !== loadRun) return;
        const message = err instanceof Error ? err.message : 'Failed to load directory tree';
        update((s) => ({ ...s, loading: false, error: message }));
        throw err;
      }
    },

    toggleResponsible(key: string) {
      update((s) => {
        const next = new Set(s.expandedResponsible);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return { ...s, expandedResponsible: next };
      });
    },

    toggleCategory(key: string) {
      update((s) => {
        const next = new Set(s.expandedCategory);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return { ...s, expandedCategory: next };
      });
    },

    toggleSubCategory(key: string) {
      update((s) => {
        const next = new Set(s.expandedSubCategory);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return { ...s, expandedSubCategory: next };
      });
    },
  };
}

export const browseTreeStore = createBrowseTreeStore();
